import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Send, X } from 'lucide-react';
import { toast } from 'sonner';

interface EmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (emailData: EmailData) => Promise<void>;
  defaultSubject?: string;
  defaultMessage?: string;
}

export interface EmailData {
  to: string;
  cc?: string;
  subject: string;
  message: string;
}

export function EmailDialog({
  open,
  onOpenChange,
  onSend,
  defaultSubject = '',
  defaultMessage = '',
}: EmailDialogProps): React.ReactElement {
  const [emailData, setEmailData] = useState<EmailData>({
    to: '',
    cc: '',
    subject: defaultSubject,
    message: defaultMessage,
  });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    // Validate email
    if (!emailData.to.trim()) {
      toast.error('Please enter a recipient email address');
      return;
    }

    // Basic email validation - uses non-capturing group to avoid ReDoS vulnerability
    // The pattern explicitly disallows dots in domain labels to prevent quadratic backtracking
    const emailRegex = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;
    const toEmails = emailData.to.split(',').map(e => e.trim());
    const invalidEmails = toEmails.filter(email => !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email address: ${invalidEmails.join(', ')}`);
      return;
    }

    if (emailData.cc) {
      const ccEmails = emailData.cc.split(',').map(e => e.trim());
      const invalidCcEmails = ccEmails.filter(email => !emailRegex.test(email));
      
      if (invalidCcEmails.length > 0) {
        toast.error(`Invalid CC email address: ${invalidCcEmails.join(', ')}`);
        return;
      }
    }

    try {
      setSending(true);
      await onSend(emailData);
      toast.success('Email sent successfully');
      onOpenChange(false);
      // Reset form
      setEmailData({
        to: '',
        cc: '',
        subject: defaultSubject,
        message: defaultMessage,
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      onOpenChange(false);
      // Reset form after a short delay
      setTimeout(() => {
        setEmailData({
          to: '',
          cc: '',
          subject: defaultSubject,
          message: defaultMessage,
        });
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Report
          </DialogTitle>
          <DialogDescription>
            Send the PDF report via email to one or more recipients.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* To Field */}
          <div className="space-y-2">
            <Label htmlFor="to">
              To <span className="text-red-500">*</span>
            </Label>
            <Input
              id="to"
              type="email"
              placeholder="recipient@example.com (separate multiple emails with commas)"
              value={emailData.to}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmailData({ ...emailData, to: e.target.value })}
              disabled={sending}
            />
            <p className="text-xs text-muted-foreground">
              Enter one or more email addresses separated by commas
            </p>
          </div>

          {/* CC Field */}
          <div className="space-y-2">
            <Label htmlFor="cc">CC (Optional)</Label>
            <Input
              id="cc"
              type="email"
              placeholder="cc@example.com (separate multiple emails with commas)"
              value={emailData.cc}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmailData({ ...emailData, cc: e.target.value })}
              disabled={sending}
            />
          </div>

          {/* Subject Field */}
          <div className="space-y-2">
            <Label htmlFor="subject">
              Subject <span className="text-red-500">*</span>
            </Label>
            <Input
              id="subject"
              type="text"
              placeholder="Email subject"
              value={emailData.subject}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmailData({ ...emailData, subject: e.target.value })}
              disabled={sending}
            />
          </div>

          {/* Message Field */}
          <div className="space-y-2">
            <Label htmlFor="message">Message (Optional)</Label>
            <Textarea
              id="message"
              placeholder="Add a message to include in the email..."
              value={emailData.message}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmailData({ ...emailData, message: e.target.value })}
              disabled={sending}
              rows={5}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={sending}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending}
          >
            <Send className="mr-2 h-4 w-4" />
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
